import { useMemo } from "react"

import { AxiosError } from "axios"
import { Formik, FormikProps } from "formik"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { STATUS_CODE_BAD_REQUEST } from "@l2r-front/l2r-query"
import { L2RButton } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { validationSchema } from "./RecoverPasswordDoneForm.schema"
import * as Styled from "./RecoverPasswordDoneForm.styled"

export const RecoverPasswordDoneForm = (props: RecoverPasswordDoneFormProps) => {
    const {
        initialValues = {
            token: "",
            password: "",
            confirmPassword: "",
        },
        onSubmit,
        className,
    } = props

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigate()

    const formikProps = useMemo(() => {
        interface FormikOnSubmitParams {
            setStatus: (status?: string) => void
        }

        interface FormikProps {
            enableReinitialize: boolean
            validationSchema: ReturnType<typeof validationSchema>
            initialValues: typeof initialValues
            onSubmit: (values: FormValues, formikHelpers: FormikOnSubmitParams) => Promise<void>
        }

        return {
            enableReinitialize: true,
            validationSchema: validationSchema(t),
            initialValues: initialValues,
            onSubmit: async (values: FormValues, { setStatus }: FormikOnSubmitParams) => {
                try {
                    await onSubmit(values)
                } catch (error: unknown) {
                    const err = error as AxiosError<{
                        password?: string[];
                        token?: string[];
                    }>

                    if (err.response?.status === STATUS_CODE_BAD_REQUEST) {
                        if (err.response.data?.password) {
                            setStatus(err.response.data.password[0])
                            return
                        }
                        if (err.response.data?.token) {
                            setStatus(err.response.data.token[0])
                            return
                        }
                    }
                    setStatus(t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.errors.unknown"))
                }
            },
        } as FormikProps
    }, [initialValues, onSubmit, t])

    return (
        <Formik<FormValues> {...formikProps}>
            {(formikState: FormikProps<FormValues>) => (
                <Styled.Form className={className}>
                    {formikState.status && (
                        <Styled.FormHelperText error id="submit-error">{formikState.status}</Styled.FormHelperText>
                    )}
                    <Styled.TokenInput
                        fullWidth
                        type="hidden"
                        name="token"
                        margin="none"
                    />
                    <Styled.PasswordInput
                        autoFocus
                        id="password"
                        type="password"
                        name="password"
                        label={t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.fields.password.label")}
                    />
                    <Styled.Text id="recover-password-done-helper" variant="Desktop & Mobile/Body/Small">
                        {t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.fields.password.helper")}
                    </Styled.Text>
                    <Styled.FormikTextField
                        id="confirm-password"
                        type="password"
                        name="confirmPassword"
                        label={t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.fields.confirmPassword.label")}
                    />
                    <Styled.ButtonsWrapper>
                        <L2RButton onClick={() => navigate("/")} variant="tertiary">
                            {t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.actions.return")}
                        </L2RButton>
                        <Styled.FormikButtonSubmit>{t(I18N_NAMESPACE, "forms.recoverPasswordDoneForm.actions.continue")}</Styled.FormikButtonSubmit>
                    </Styled.ButtonsWrapper>
                </Styled.Form>
            )}
        </Formik>
    )
}

export type FormValues = {
    token: string,
    password: string,
    confirmPassword: string,
}

type RecoverPasswordDoneFormProps = {
    initialValues: FormValues
    onSubmit: (values: FormValues) => Promise<void>,
    className: string,
}